import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
interface Plan {
  name: string;
  badge?: string;
  recommendedFor?: string;
  price: React.ReactNode;
  subtitle: string;
  features: string[];
  cta: string;
}
const plans: Plan[] = [{
  name: 'Basic',
  price: <span className="text-4xl font-bold text-slate-900" data-id="element-240">Free</span>,
  subtitle: 'No credit card required',
  features: ['10 eligibility checks per month', 'Real-time eligibility verification', 'Full eligibility report access'],
  cta: 'Get Started Free'
}, {
  name: 'Clinic',
  badge: 'Pay As You Go',
  recommendedFor: 'Small Practices',
  price: <div className="flex items-baseline gap-1.5" data-id="element-241">
        <span className="text-sm text-slate-500 font-medium" data-id="element-242">
          Starting around
        </span>
        <span className="text-4xl font-bold text-slate-900" data-id="element-243">$1</span>
        <span className="text-slate-500 font-medium text-sm" data-id="element-244">/check</span>
      </div>,
  subtitle: 'Pay only for what you use',
  features: ['Unlimited eligibility checks', 'No monthly minimums', 'Monthly billing', 'Volume discounts as you grow'],
  cta: 'Start Verifying'
}, {
  name: 'Clinic Plus',
  recommendedFor: 'Medium Practices',
  price: <div className="flex items-baseline gap-1.5" data-id="element-245">
        <span className="text-4xl font-bold text-slate-900" data-id="element-246">$129</span>
        <span className="text-slate-500 font-medium text-sm" data-id="element-247">/month</span>
      </div>,
  subtitle: 'Includes 200 eligibility checks',
  features: ['Lower effective per-check cost', 'Priority support', 'Volume discounts for additional usage', 'Everything in Clinic'],
  cta: 'Get Clinic Plus'
}, {
  name: 'Clinic Ultra',
  badge: 'Custom',
  price: <span className="text-4xl font-bold text-slate-900" data-id="element-248">Custom</span>,
  subtitle: 'Tailored pricing and support',
  features: ['Custom pricing based on volume', 'Dedicated account support', 'Designed for high-volume orgs', 'Everything in Clinic Plus'],
  cta: 'Contact Us'
}];
export function PricingPreview() {
  return <section id="pricing" className="py-20 lg:py-28 bg-brand-bg" data-id="element-249">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-250">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-251">
          <motion.h2 {...fadeUp} className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-252">
            Simple, Transparent Pricing That Scales With You
          </motion.h2>
          <motion.p {...fadeUp} transition={{
          delay: 0.1
        }} className="text-slate-600 text-lg" data-id="element-253">
            Pricing scales with your usage — the more you verify, the lower your
            effective cost.
          </motion.p>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 max-w-6xl mx-auto" data-id="element-254">
          {plans.map((plan, i) => <motion.div key={plan.name} {...fadeUp} transition={{
          delay: i * 0.08
        }} className="relative rounded-2xl p-7 flex flex-col bg-white border border-slate-200 shadow-sm" data-id="element-255">
              {/* Recommended For Sticker */}
              {plan.recommendedFor && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold tracking-wide bg-emerald-500 text-white shadow-sm" data-id="element-256">
                  {plan.recommendedFor}
                </div>}

              {/* Badge */}
              {plan.badge && !plan.recommendedFor && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold tracking-wide bg-emerald-500 text-white shadow-sm" data-id="element-257">
                  {plan.badge}
                </div>}

              {/* Plan Name */}
              <h3 className={`text-lg font-bold mb-4 ${plan.badge || plan.recommendedFor ? 'mt-2' : ''} text-slate-900`} data-id="element-258">
                {plan.name}
              </h3>

              {/* Price */}
              <div className="mb-1" data-id="element-259">{plan.price}</div>
              <p className="text-sm text-slate-500 mb-6" data-id="element-260">{plan.subtitle}</p>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1" data-id="element-261">
                {plan.features.map((feature, fi) => <li key={fi} className="flex items-start gap-2.5" data-id="element-262">
                    <CheckIcon className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" data-id="element-263" />
                    <span className="text-sm text-slate-700" data-id="element-264">{feature}</span>
                  </li>)}
              </ul>

              {/* CTA */}
              <button className="w-full py-3 rounded-xl font-semibold text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-teal bg-brand-green hover:bg-brand-green/90 text-white" data-id="element-265">
                {plan.cta}
              </button>
            </motion.div>)}
        </div>

        {/* Trust line */}
        <motion.div {...fadeUp} transition={{
        delay: 0.4
      }} className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 mt-10 text-sm text-slate-500 font-medium" data-id="element-266">
          <span data-id="element-267">No contracts</span>
          <span className="text-slate-300" data-id="element-268">•</span>
          <span data-id="element-269">No monthly minimums</span>
          <span className="text-slate-300" data-id="element-270">•</span>
          <span data-id="element-271">Cancel anytime</span>
        </motion.div>
      </div>
    </section>;
}